'use client'
import { memo, type ReactNode, type FC, type MouseEvent } from 'react'
import styled, { css } from 'styled-components'
import type { TypeColors } from '@/schema'
import { mq, presets, vw } from '@/styles'

type Props = {
  bgColor?: TypeColors
  children: ReactNode
  className?: string
  color?: TypeColors
  disabled?: boolean
  id?: string
  onClick?: (event: MouseEvent<HTMLButtonElement>) => void
  outline?: boolean
  type?: 'button' | 'submit'
}

type ButtonProps = {
  $bgColor?: Props['bgColor']
  $color: Props['color']
  $outline: Props['outline']
}

type ButtonBgHoverProps = {
  $bgColor?: Props['bgColor']
  $bgColorHover?: Props['bgColor']
  $outline: Props['outline']
}

const getTextColorByBgColor = (bgColor) => {
  return bgColor.includes('background') || bgColor.includes('white') ? '--color-text--darker' : '--color-white'
}

const getTextColorByBgColorHover = (bgColor) => {
  return !bgColor || bgColor.includes('background') || bgColor.includes('white') ? '--color-white' : (bgColor.includes('text') && !bgColor.includes('yellow')) ? '--color-text--yellow' : '--color-text--darker'
}

// MAMMAFIORE
// color: ${ ({ theme, $bgType, $outline }) => $outline ?
//    theme.colors[$bgType] :
//    theme.colors?.[$bgType === 'background' ? 'darkred' : $bgType === 'creme' || $bgType === 'white' ? 'red' : 'white'] };

// HELLOMAMMA
// color: ${ ({ theme, bgType, outline }) => outline ?
//    handleBgType(bgType, theme)
//    : theme.colors[bgType === 'background' ? 'greendark' : bgType === 'white' ? 'greenlight' : 'white'] };

export const Button: FC<Props> = memo(({ bgColor = '--color-text--darker', children, className = '', color, disabled = false, id, onClick, outline = false, type = 'button' }) => {
  const textColor = color ?? outline ? color : getTextColorByBgColor(bgColor)
  return (
    <Btn type={type} {...id && { id }} onClick={onClick} {...className && { className }} disabled={disabled} $color={textColor} $bgColor={outline ? null : bgColor} $outline={outline}>
      <BgHover $bgColor={outline ? null : bgColor} $bgColorHover={getTextColorByBgColorHover(bgColor)} $outline={outline} />
      <span>{children}</span>
    </Btn>
  )
})

const Btn = styled.button<ButtonProps>`
  ${presets.font15}
  align-items: center;
  background-color: ${({ $bgColor }) => $bgColor ? `var(${$bgColor})` : 'transparent'};
  border-radius: ${vw(30, 'mobile')};
  color: var(${({ $color }) => $color});
  display: flex;
  font-family: var(--font-graphik);
  justify-content: center;
  letter-spacing: .13px;
  margin: 0 auto;
  padding: ${vw(17, 'mobile')} ${vw(30, 'mobile')};
  position: relative;
  transition: 250ms background-color ease-out, 250ms color ease-out, 250ms opacity ease-out;
  width: unset;
  z-index: auto;

  ${mq.greaterThan('nexus7')} {
    border-radius:${vw(30, 'tablet')};
    padding: ${vw(17, 'tablet')} ${vw(30, 'tablet')};
  }

  ${mq.greaterThan('tablet')} {
    ${presets.font18_4}
    border-radius: ${vw(30, 'desktop')};
    letter-spacing: 0;
    padding: ${vw(19, 'desktop')} ${vw(43, 'desktop')};
  }

  ${mq.greaterThan('desktop')} {
    border-radius: 30px;
    padding: 19px 43px;
  }

  &[type='submit'] {
    ${mq.greaterThan('tablet')} {
      font-weight: 500;
    }
  }

  &[disabled] {
    cursor: not-allowed;
    opacity: .35;
    pointer-events: none;
  }

  span {
    margin: 0 !important;
    padding: 0;
    z-index: 2;
  }

  > div {
    border: ${({ $color, $outline }) => $outline ? `1px solid var(${$color})` : 'none'};
  }

  &:hover {
    > div {
      ${mq.greaterThan('tablet')} {
        transform: scale3d(1.1, 1.2, 1);
      }

      ${({ $outline }) => !$outline && css`
        &:before {
          transition: transform .4s cubic-bezier(.1, 0, .3, 1);
          transform: translate3d(-50%, -50%, 0) scale3d(1, 1, 1);
        }

        &:after {
          opacity: 1;
          transition-duration: .01s;
          transition-delay: .3s;
        }
      `}
    }
  }
`

export const BgHover = styled.div<ButtonBgHoverProps>`
  background-color: ${({ $bgColor }) => $bgColor ? `var(${$bgColor})` : 'inherit'};
  border: ${({ $bgColorHover, $outline }) => $outline ? `1px solid var(${$bgColorHover})` : 'none'};
  height: 100%;
  left: 0;
  overflow: hidden;
  position: absolute;
  top: 0;
  transition: transform .4s cubic-bezier(.1, 0, .3, 1);
  width: 100%;

  &,
  &:before {
    border-radius:${vw(30, 'mobile')};

    ${mq.greaterThan('nexus7')} {
      border-radius:${vw(30, 'tablet')};
    }

    ${mq.greaterThan('tablet')} {
      border-radius:${vw(30, 'desktop')};
    }

    ${mq.greaterThan('desktop')} {
      border-radius: 30px;
    }
  }

  &:before,
  &:after {
    background-color: var(${({ $bgColorHover }) => $bgColorHover});
    content: '';
    position: absolute;
    z-index: -1;
  }

  &:before {
    border-radius: 50%;
    height: 0;
    left: 50%;
    padding-bottom: 110%;
    top: 50%;
    transform: translate3d(-50%,-50%,0) scale3d(0,0,1);
    width: 110%;
  }

  &:after {
    height: 100%;
    left: 0;
    opacity: 0;
    top: 0;
    transition: 300ms opacity;
    width: 100%;
  }
`
